/**
 * Event System API Service
 * Manages API calls for the event system (templates, flows, actions, devices)
 */
import { get, post, put, del } from './apiService';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';

// Debug logging gate for Event System (enable in DevTools via: window.__DEBUG_EVENT_SYSTEM__ = true)
const ES_DEBUG = typeof window !== 'undefined' && window.__DEBUG_EVENT_SYSTEM__ === true;
const dlog = (...args) => {
  if (ES_DEBUG) console.log('[ES]', ...args);
};

// Use API_ROUTES from the centralized constants file
// This ensures consistency across the application

// Fallback settings - determines if we try the API first or just use Firestore
// Set this to true now that the backend API supports tenant filtering
const USE_API_FIRST = true;

/**
 * Validate and normalize tenant ID (explicit, no fallbacks)
 * @param {string} tenantId
 * @returns {string} trimmed tenantId
 * @throws Error if missing/invalid
 */
const ensureTenantId = tenantId => {
  if (!tenantId || typeof tenantId !== 'string' || tenantId.trim().length === 0) {
    throw new Error('tenantId is required for Event System API call');
  }
  return tenantId.trim();
};

/**
 * Helper function to append tenantId to API URL if provided
 * @param {string} baseUrl - The base API URL
 * @param {string} tenantId - Optional tenant ID to filter by
 * @param {Object} additionalParams - Optional additional query parameters
 * @returns {string} URL with query parameters
 */
const appendTenantId = (baseUrl, tenantId, additionalParams = {}) => {
  // Validate tenant ID (required)
  const validTenantId = ensureTenantId(tenantId);

  const params = new URLSearchParams();

  // Add tenant ID
  params.append('tenantId', validTenantId);
  dlog(`params: tenantId=${validTenantId} for ${baseUrl}`);

  // Add any additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
      dlog(`params: ${key}=${value} for ${baseUrl}`);
    }
  });

  const queryString = params.toString();
  const fullUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
  dlog(`url: ${fullUrl}`);
  return fullUrl;
};

/**
 * Helper function to add tenantId to request body if provided
 * @param {Object} data - The request body data
 * @param {string} tenantId - Optional tenant ID to include
 * @returns {Object} Data with tenantId added
 */
const addTenantToBody = (data, tenantId) => {
  const validTenantId = ensureTenantId(tenantId);
  return { ...data, tenantId: validTenantId };
};

// Event Templates API
export const templateApi = {
  getAll: async (tenantId, activeOnly = false) => {
    if (!USE_API_FIRST) {
      dlog('skip /templates (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(API_ROUTES.EVENT_TEMPLATES, tenantId, { active_only: activeOnly });
    dlog(`GET templates tenant=${ensureTenantId(tenantId)}`);
    try {
      return await get(url);
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  },

  getById: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip /templates/${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.EVENT_TEMPLATES}/${id}`, tenantId);
    try {
      return await get(url);
    } catch (error) {
      console.error(`Error fetching template ${id}:`, error);
      return { success: true, data: null };
    }
  },

  create: async (templateData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip create template (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(templateData, tenantId);
    return post(API_ROUTES.EVENT_TEMPLATES, data);
  },

  update: async (id, templateData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip update template ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(templateData, tenantId);
    return put(`${API_ROUTES.EVENT_TEMPLATES}/${id}`, data);
  },

  delete: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip delete template ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.EVENT_TEMPLATES}/${id}`, tenantId);
    return del(url);
  },
};

// Event Flows API
export const flowApi = {
  getAll: async (tenantId, templateId = null) => {
    if (!USE_API_FIRST) {
      dlog('skip /flows (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }

    // Get safe tenant ID
    const safeTenantId = ensureTenantId(tenantId);
    dlog(`flows tenant=${safeTenantId}`);

    try {
      // Construct URL with tenant ID parameter and optional templateId
      const url = appendTenantId(API_ROUTES.FLOWS, safeTenantId, { templateId });
      dlog('GET flows');

      // Make API request
      const response = await get(url);
      dlog(`flows ok n=${Array.isArray(response?.data) ? response.data.length : 'unknown'}`);
      return response;
    } catch (error) {
      console.error('[ES][flows:getAll]', error.status || 'net', error.message);
      throw error;
    }
  },

  getById: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip /flows/${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.FLOWS}/${id}`, tenantId);
    try {
      return await get(url);
    } catch (error) {
      console.error(`Error fetching flow ${id}:`, error);
      return { success: true, data: null };
    }
  },

  create: async (flowData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip create flow (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(flowData, tenantId);
    return post(API_ROUTES.FLOWS, data);
  },

  update: async (id, flowData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip update flow ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(flowData, tenantId);
    return put(`${API_ROUTES.FLOWS}/${id}`, data);
  },

  delete: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip delete flow ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.FLOWS}/${id}`, tenantId);
    return del(url);
  },
};

// Actions API (for event system actions)
export const actionApi = {
  getAll: async (tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip /actions (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }

    // Get safe tenant ID
    const safeTenantId = ensureTenantId(tenantId);
    dlog(`actions tenant=${safeTenantId}`);

    try {
      // Construct URL with tenant ID parameter
      const url = appendTenantId(API_ROUTES.ACTIONS, safeTenantId);
      dlog('GET actions');

      // Make API request
      const response = await get(url);
      dlog(`actions ok n=${Array.isArray(response?.data) ? response.data.length : 'unknown'}`);
      return response;
    } catch (error) {
      console.error('[ES][actions:getAll]', error.status || 'net', error.message);
      throw error;
    }
  },

  getById: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip /actions/${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.ACTIONS}/${id}`, tenantId);
    try {
      return await get(url);
    } catch (error) {
      console.error(`Error fetching action ${id}:`, error);
      return { success: true, data: null };
    }
  },

  create: async (actionData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip create action (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(actionData, tenantId);
    return post(API_ROUTES.ACTIONS, data);
  },

  update: async (id, actionData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip update action ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(actionData, tenantId);
    return put(`${API_ROUTES.ACTIONS}/${id}`, data);
  },

  delete: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip delete action ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.ACTIONS}/${id}`, tenantId);
    return del(url);
  },

  dispatch: async (actionData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip dispatch action (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(actionData, tenantId);
    return post(API_ROUTES.ACTIONS_DISPATCH, data);
  },
};

// Devices API (for test devices)
export const deviceApi = {
  getAll: async (tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip /devices (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }

    // Get safe tenant ID
    const safeTenantId = ensureTenantId(tenantId);
    dlog(`devices tenant=${safeTenantId}`);

    try {
      // Construct URL with tenant ID parameter
      const url = appendTenantId(API_ROUTES.DEVICES, safeTenantId);
      dlog('GET devices');

      // Make API request
      const response = await get(url);
      dlog(`devices ok n=${Array.isArray(response?.data) ? response.data.length : 'unknown'}`);
      return response;
    } catch (error) {
      console.error('[ES][devices:getAll]', error.status || 'net', error.message);
      throw error;
    }
  },

  getById: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip /devices/${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.DEVICES}/${id}`, tenantId);
    try {
      return await get(url);
    } catch (error) {
      console.error(`Error fetching device ${id}:`, error);
      return { success: true, data: null };
    }
  },

  create: async (deviceData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip create device (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(deviceData, tenantId);
    return post(API_ROUTES.DEVICES, data);
  },

  update: async (id, deviceData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip update device ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const data = addTenantToBody(deviceData, tenantId);
    return put(`${API_ROUTES.DEVICES}/${id}`, data);
  },

  delete: async (id, tenantId) => {
    if (!USE_API_FIRST) {
      dlog(`skip delete device ${id} (USE_API_FIRST=false)`);
      return Promise.reject('API_SKIPPED');
    }
    const url = appendTenantId(`${API_ROUTES.DEVICES}/${id}`, tenantId);
    return del(url);
  },
};

// Alert API (for testing and triggering alerts)
export const alertApi = {
  sendAlert: async (alertData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip /alerts/send (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    return post(API_ROUTES.ALERTS_SEND, addTenantToBody(alertData, tenantId));
  },

  testAlert: async (testData, tenantId) => {
    if (!USE_API_FIRST) {
      dlog('skip /alert/test (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    return post(API_ROUTES.ALERT_TEST, addTenantToBody(testData, tenantId));
  },

  acknowledgeAlert: async ({ tenantId, alertId, userId, notes } = {}) => {
    if (!USE_API_FIRST) {
      dlog('skip /alerts/acknowledge (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    const payload = {
      tenantId: ensureTenantId(tenantId),
      alertId,
    };
    if (userId) payload.userId = userId;
    if (notes) payload.notes = notes;
    return post(API_ROUTES.ALERTS_ACKNOWLEDGE, payload);
  },

  getAlertLogs: async (filters = {}) => {
    if (!USE_API_FIRST) {
      console.log('Skipping API call to /alert-logs (USE_API_FIRST=false)');
      return Promise.reject('API_SKIPPED');
    }
    // Ensure tenantId present
    const queryParams = new URLSearchParams();
    const tenantId = ensureTenantId(filters?.tenantId);
    queryParams.append('tenantId', tenantId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && key !== 'tenantId') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return get(`${API_ROUTES.ALERT_LOGS}${queryString ? `?${queryString}` : ''}`);
  },
};

// Stable Event System API functions for EventSystemStable component
export const getEventSystemDevices = async (tenantId) => {
  const response = await deviceApi.getAll(tenantId);
  return response?.data || [];
};

export const getEventSystemActions = async tenantId => {
  const response = await actionApi.getAll(tenantId);
  return response?.data || [];
};

export const getEventSystemFlows = async tenantId => {
  const response = await flowApi.getAll(tenantId);
  return response?.data || [];
};
